import type * as React from 'react'
import type { JSX } from 'react'

import { useCommandState } from 'cmdk'

import {
  Button,
  Combobox,
  EmptyState,
  Flex,
  LockIcon,
  SearchIcon,
  Tag as TagComponent,
  TagIcon,
} from '@buffer-mono/popcorn'
import type {
  ComboboxContentProps,
  ComboboxAnchorProps,
} from '@buffer-mono/popcorn'

import styles from './TagsCombobox.module.css'

export type TagsComboboxViewTag = Readonly<{
  id: string
  name: string
  color: string
  isLocked?: boolean
}>

export type TagsComboboxViewProps = {
  tags: readonly TagsComboboxViewTag[]
  selectedTags: string[]
  onChange: (tags: string[]) => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children?: React.ReactNode
  limit: number
  canCreate: boolean
  onCreate: (name: string) => void
  createFromSearchWithoutTags?: boolean
  footer?: React.ReactNode
  align?: ComboboxContentProps['align']
  side?: ComboboxContentProps['side']
  anchor?: ComboboxAnchorProps['virtualRef']
  className?: string
}

export function TagsComboboxView({
  tags,
  open,
  selectedTags,
  onChange,
  onOpenChange,
  children,
  limit,
  canCreate,
  onCreate,
  createFromSearchWithoutTags = false,
  footer,
  align,
  side,
  anchor,
  className,
}: TagsComboboxViewProps): JSX.Element {
  const limitReached = selectedTags.length >= limit

  return (
    <Combobox
      open={open}
      value={selectedTags}
      onChange={onChange}
      multiple
      onOpenChange={onOpenChange}
    >
      {anchor && <Combobox.Anchor virtualRef={anchor} />}
      <Combobox.Trigger>{children}</Combobox.Trigger>
      <Combobox.Content align={align} side={side} className={className}>
        <Combobox.Input placeholder="Search tags" />
        {!!tags.length && (
          <>
            {/* Guard the results list: filter-as-you-type unmounts non-matching
                tag rows, so a browser-translated tag name would crash React on
                removeChild. The search input + footer stay translatable. See
                FilterByTag. */}
            <Combobox.List translate="no">
              <Combobox.Empty>
                <EmptyState size="small">
                  <EmptyState.Icon>
                    <SearchIcon />
                  </EmptyState.Icon>
                  <EmptyState.Description>No tags found</EmptyState.Description>
                </EmptyState>
              </Combobox.Empty>
              <Combobox.Group>
                {tags.map((tag) => {
                  const disabled =
                    tag.isLocked ||
                    (limitReached && !selectedTags.includes(tag.id))
                  return (
                    <Combobox.Item
                      key={tag.id}
                      value={tag.id}
                      keywords={[tag.name]}
                      disabled={disabled}
                    >
                      <Flex justify="between" className={styles.tagItem}>
                        <TagComponent key={tag.id} color={tag.color}>
                          {tag.name}
                        </TagComponent>
                        {tag.isLocked && <LockIcon />}
                      </Flex>
                    </Combobox.Item>
                  )
                })}
              </Combobox.Group>
              {canCreate && <Combobox.Create onSelect={onCreate} />}
            </Combobox.List>

            {footer}
          </>
        )}
        {!tags.length && (
          <Combobox.List>
            <EmptyState size="small">
              <EmptyState.Icon>
                <TagIcon />
              </EmptyState.Icon>
              <EmptyState.Description>
                There are no tags yet.
              </EmptyState.Description>
              {canCreate && (
                <EmptyState.Actions>
                  <NewTagFromSearchButton onCreate={onCreate} />
                </EmptyState.Actions>
              )}
            </EmptyState>
            {canCreate && createFromSearchWithoutTags && (
              <Combobox.Create onSelect={onCreate} />
            )}
          </Combobox.List>
        )}
      </Combobox.Content>
    </Combobox>
  )
}

function NewTagFromSearchButton({
  onCreate,
}: {
  onCreate: (name: string) => void
}): JSX.Element {
  const search = useCommandState((state) => state.search)
  return <Button onClick={(): void => onCreate(search.trim())}>New tag</Button>
}
